import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { PageHeader, Card, Button, Badge } from '../../ui';

const API_BASE = (process.env.REACT_APP_API_URL || '/api') + '/v1';
const ORIGIN = typeof window !== 'undefined' ? window.location.origin : 'https://app.teve.ca';
const FULL_BASE = `${ORIGIN}/api/v1`;

const METHOD_TONE = { get: 'good', post: 'brand', put: 'warn', patch: 'warn', delete: 'live' };

const SAMPLES = {
  curl: `# List your streams
curl ${FULL_BASE}/streams \\
  -H "Authorization: Bearer tev_live_YOUR_KEY"

# Create a stream (needs a write-scoped key)
curl -X POST ${FULL_BASE}/streams \\
  -H "Authorization: Bearer tev_live_YOUR_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"name":"My Channel","description":"Launch stream"}'`,
  fetch: `const BASE = "${FULL_BASE}";
const KEY = "tev_live_YOUR_KEY"; // or a user JWT from /auth/login

async function listStreams() {
  const res = await fetch(\`\${BASE}/streams\`, {
    headers: { Authorization: \`Bearer \${KEY}\` },
  });
  if (!res.ok) throw new Error((await res.json()).error?.message);
  const { data } = await res.json();
  return data;
}`,
  axios: `import axios from "axios";

const teve = axios.create({
  baseURL: "${FULL_BASE}",
  headers: { Authorization: \`Bearer \${process.env.TEVE_API_KEY}\` },
});

// Mobile / React user session instead of a key:
// const { data } = await teve.post("/auth/login", { email, password });
// teve.defaults.headers.Authorization = \`Bearer \${data.token}\`;

const { data } = await teve.get("/streams");
console.log(data.data);`,
};

const CodeBlock = ({ text }) => (
  <pre className="tw-overflow-x-auto tw-rounded-lg tw-border tw-border-line tw-bg-sunken tw-p-4 tw-text-xs tw-leading-relaxed tw-text-content">
    <code>{text}</code>
  </pre>
);

const DeveloperHub = () => {
  const [spec, setSpec] = useState(null);
  const [err, setErr] = useState(false);
  const [lang, setLang] = useState('curl');
  const [open, setOpen] = useState({});

  useEffect(() => {
    axios.get(`${API_BASE}/openapi.json`)
      .then(({ data }) => setSpec(data))
      .catch(() => setErr(true));
  }, []);

  const groups = useMemo(() => {
    if (!spec) return [];
    const byTag = {};
    const tagOrder = (spec.tags || []).map((t) => t.name);
    const tagDesc = Object.fromEntries((spec.tags || []).map((t) => [t.name, t.description]));
    Object.entries(spec.paths).forEach(([path, ops]) => {
      Object.entries(ops).forEach(([method, op]) => {
        if (!op || typeof op !== 'object') return;
        const tag = (op.tags && op.tags[0]) || 'Other';
        (byTag[tag] = byTag[tag] || []).push({
          method, path, summary: op.summary || '',
          scope: op['x-scope'] === 'write' ? 'write' : 'read',
          open: Array.isArray(op.security) && op.security.length === 0,
        });
      });
    });
    return Object.keys(byTag)
      .sort((a, b) => {
        const ia = tagOrder.indexOf(a); const ib = tagOrder.indexOf(b);
        return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
      })
      .map((tag) => ({ tag, desc: tagDesc[tag], endpoints: byTag[tag] }));
  }, [spec]);

  return (
    <>
      <PageHeader
        title="Developer / API"
        subtitle="One REST API for the whole platform — streams, media, scheduling, billing, monetization, domains, analytics and more. Built for mobile apps and React front-ends."
        actions={
          <>
            <Button variant="secondary" icon="fa-book" as="a" href={`${FULL_BASE}/docs`} target="_blank" rel="noreferrer">
              Full reference
            </Button>
            <Button as="a" href={`${FULL_BASE}/openapi.json`} target="_blank" rel="noreferrer" variant="secondary" icon="fa-file-code">
              OpenAPI spec
            </Button>
            <Link to="/developer/api-keys"><Button icon="fa-key">API Keys</Button></Link>
          </>
        }
      />

      <div className="tw-grid tw-gap-6 lg:tw-grid-cols-3">
        <Card title="Base URL" className="lg:tw-col-span-1" bodyClass="tw-text-sm">
          <code className="tw-block tw-break-all tw-rounded tw-bg-sunken tw-px-2 tw-py-1.5 tw-text-xs">{FULL_BASE}</code>
          <p className="tw-mt-2 tw-text-content-muted">Every endpoint below is relative to this. The unversioned <code>/api/…</code> routes behave identically.</p>
        </Card>

        <Card title="Authentication" className="lg:tw-col-span-2" bodyClass="tw-text-sm tw-space-y-3">
          <p className="tw-text-content-muted">Send either credential the same way:</p>
          <code className="tw-block tw-rounded tw-bg-sunken tw-px-2 tw-py-1.5 tw-text-xs">Authorization: Bearer &lt;token&gt;</code>
          <div className="tw-grid tw-gap-3 sm:tw-grid-cols-2">
            <div className="tw-rounded-lg tw-border tw-border-line tw-p-3">
              <div className="tw-font-semibold tw-text-heading"><i className="fa-solid fa-key tw-mr-1.5 tw-text-brand" />API key</div>
              <p className="tw-mt-1 tw-text-content-muted"><code>tev_live_…</code> — for servers &amp; back-ends. Scope <Badge tone="neutral">read</Badge> or <Badge tone="warn">write</Badge>. Manage in <Link to="/developer/api-keys" className="tw-text-brand">API Keys</Link>. May also be sent as <code>X-API-Key</code>.</p>
            </div>
            <div className="tw-rounded-lg tw-border tw-border-line tw-p-3">
              <div className="tw-font-semibold tw-text-heading"><i className="fa-solid fa-user tw-mr-1.5 tw-text-brand" />User JWT</div>
              <p className="tw-mt-1 tw-text-content-muted">From <code>POST /auth/login</code>, <code>/auth/google</code> or <code>/auth/apple</code> — for mobile apps and browser sessions. Inherits the user's role and plan.</p>
            </div>
          </div>
          <p className="tw-text-content-faint tw-text-xs">No credential needed for <code>/public/*</code>, <code>/auth/*</code>, <code>/subscription/plans</code>, <code>/settings/public</code>, <code>/settings/social-public</code> and <code>/branding</code>.</p>
        </Card>
      </div>

      <Card
        title="Quick start"
        className="tw-mt-6"
        actions={
          <div className="tw-flex tw-gap-1">
            {Object.keys(SAMPLES).map((k) => (
              <button
                key={k}
                onClick={() => setLang(k)}
                className={`tw-rounded-md tw-px-2.5 tw-py-1 tw-text-xs tw-font-medium tw-capitalize ${lang === k ? 'tw-bg-brand tw-text-white' : 'tw-bg-sunken tw-text-content-muted'}`}
              >
                {k}
              </button>
            ))}
          </div>
        }
      >
        <CodeBlock text={SAMPLES[lang]} />
      </Card>

      <div className="tw-mt-6 tw-grid tw-gap-4 sm:tw-grid-cols-3">
        {[
          ['Responses', 'Versioned resources return { data, meta }. Errors are { error: { code, message } } with a matching HTTP status.'],
          ['Rate limits', '120 requests/min per key or token; 60/min on public endpoints. 429 with Retry-After when exceeded.'],
          ['White-label', 'GET /branding resolves brand + logo from the request Host. "Powered by Siman Tech" and copyright are fixed.'],
        ].map(([t, d]) => (
          <div key={t} className="tw-rounded-lg tw-border tw-border-line tw-bg-surface tw-p-4 tw-text-sm">
            <div className="tw-font-semibold tw-text-heading">{t}</div>
            <p className="tw-mt-1 tw-text-content-muted">{d}</p>
          </div>
        ))}
      </div>

      <h2 className="tw-mt-8 tw-mb-3 tw-text-lg tw-font-semibold tw-text-heading">Endpoints</h2>

      {err && (
        <Card bodyClass="tw-text-sm tw-text-content-muted">
          Couldn't load the live spec. Open the <a className="tw-text-brand" href={`${FULL_BASE}/docs`} target="_blank" rel="noreferrer">full reference</a> instead.
        </Card>
      )}
      {!spec && !err && (
        <div className="tw-py-10 tw-text-center"><i className="fa-solid fa-spinner fa-spin tw-text-brand" /></div>
      )}

      <div className="tw-space-y-3">
        {groups.map(({ tag, desc, endpoints }) => {
          const isOpen = open[tag] ?? false;
          return (
            <Card key={tag} noPad>
              <button
                onClick={() => setOpen((o) => ({ ...o, [tag]: !isOpen }))}
                className="tw-flex tw-w-full tw-items-center tw-justify-between tw-px-4 tw-py-3 tw-text-left"
              >
                <span>
                  <span className="tw-font-semibold tw-text-heading">{tag}</span>
                  <span className="tw-ml-2 tw-text-xs tw-text-content-faint">{endpoints.length}</span>
                  {desc && <span className="tw-mt-0.5 tw-block tw-text-xs tw-text-content-muted">{desc}</span>}
                </span>
                <i className={`fa-solid fa-chevron-${isOpen ? 'up' : 'down'} tw-text-content-faint`} />
              </button>
              {isOpen && (
                <div className="tw-border-t tw-border-line">
                  {endpoints.map((e, i) => (
                    <div key={i} className="tw-flex tw-items-center tw-gap-3 tw-border-b tw-border-line/60 tw-px-4 tw-py-2 last:tw-border-0">
                      <Badge tone={METHOD_TONE[e.method] || 'neutral'} className="tw-w-16 tw-text-center tw-uppercase">{e.method}</Badge>
                      <code className="tw-flex-1 tw-text-xs tw-text-content">{e.path}</code>
                      {e.open
                        ? <Badge tone="neutral">public</Badge>
                        : <Badge tone={e.scope === 'write' ? 'warn' : 'neutral'}>{e.scope}</Badge>}
                      <span className="tw-hidden tw-flex-1 tw-text-xs tw-text-content-muted md:tw-block">{e.summary}</span>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          );
        })}
      </div>
    </>
  );
};

export default DeveloperHub;
